import { useState, useEffect, useCallback, useRef } from 'react'
import { X, Target, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface VisualSelectorPickerProps {
  storeUrl: string
  onSelect: (selector: string) => void
  onCancel: () => void
}

/**
 * VisualSelectorPicker - Lightweight element selector overlay
 *
 * Opens a store URL in an iframe and allows click-to-select for CSS selectors.
 * Unlike the full visual editor, this ONLY selects elements - no modifications.
 *
 * Usage:
 * ```tsx
 * <VisualSelectorPicker
 *   storeUrl="https://example.mitiendanube.com"
 *   onSelect={(selector) => console.log('Selected:', selector)}
 *   onCancel={() => console.log('Cancelled')}
 * />
 * ```
 */
export function VisualSelectorPicker({ storeUrl, onSelect, onCancel }: VisualSelectorPickerProps) {
  const [selectedElement, setSelectedElement] = useState<HTMLElement | null>(null)
  const [hoveredElement, setHoveredElement] = useState<HTMLElement | null>(null)
  const [selector, setSelector] = useState<string>('')
  const [isReady, setIsReady] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // Generate CSS selector for element
  const generateSelector = useCallback((element: HTMLElement): string => {
    // Priority 1: Stable ID
    if (element.id && !element.id.match(/^(ember|react|vue|ng|component)/i)) {
      const idSelector = `#${element.id}`
      if (element.ownerDocument.querySelectorAll(idSelector).length === 1) {
        return idSelector
      }
    }

    // Priority 2: TiendaNube-specific classes
    const tnClasses = [
      'js-addtocart', 'js-prod-variant', 'js-product-link',
      'product-price', 'product-title', 'btn-primary', 'btn-secondary'
    ]

    for (const tnClass of tnClasses) {
      if (element.classList.contains(tnClass)) {
        const classSelector = `.${tnClass}`
        if (element.ownerDocument.querySelectorAll(classSelector).length === 1) {
          return classSelector
        }
      }
    }

    // Priority 3: First 3 classes (if unique)
    if (element.classList.length > 0) {
      const classes = Array.from(element.classList).slice(0, 3)
      const classSelector = `.${classes.join('.')}`
      if (element.ownerDocument.querySelectorAll(classSelector).length === 1) {
        return classSelector
      }
    }

    // Priority 4: Path selector (fallback)
    const path: string[] = []
    let current: HTMLElement | null = element

    while (current && current !== element.ownerDocument.body) {
      let selector = current.tagName.toLowerCase()

      if (current.id && !current.id.match(/^(ember|react|vue|ng|component)/i)) {
        selector = `#${current.id}`
        path.unshift(selector)
        break
      }

      // Add nth-child for specificity
      const siblings = current.parentElement?.children
      if (siblings && siblings.length > 1) {
        const index = Array.from(siblings).indexOf(current) + 1
        selector += `:nth-child(${index})`
      }

      path.unshift(selector)
      current = current.parentElement
    }

    return path.join(' > ')
  }, [])

  // Handle iframe load
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const handleLoad = () => {
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document
      if (!iframeDoc) return

      // Inject hover/selection styles
      const style = iframeDoc.createElement('style')
      style.textContent = `
        .tnx-selector-hover {
          outline: 2px solid rgba(34, 197, 94, 0.6) !important;
          outline-offset: 2px !important;
          cursor: crosshair !important;
        }
        .tnx-selector-selected {
          outline: 3px solid rgb(59, 130, 246) !important;
          outline-offset: 2px !important;
        }
      `
      iframeDoc.head.appendChild(style)

      // Add event listeners
      const handleMouseOver = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const target = e.target as HTMLElement
        if (!target || target === iframeDoc.body) return

        // Clear previous hover
        if (hoveredElement && hoveredElement !== selectedElement) {
          hoveredElement.classList.remove('tnx-selector-hover')
        }

        // Apply hover
        if (target !== selectedElement) {
          target.classList.add('tnx-selector-hover')
          setHoveredElement(target)
        }
      }

      const handleClick = (e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()

        const target = e.target as HTMLElement
        if (!target || target === iframeDoc.body) return

        // Clear previous selection
        if (selectedElement) {
          selectedElement.classList.remove('tnx-selector-selected')
        }

        // Clear hover
        target.classList.remove('tnx-selector-hover')

        // Apply selection
        target.classList.add('tnx-selector-selected')
        setSelectedElement(target)

        // Generate selector
        const generatedSelector = generateSelector(target)
        setSelector(generatedSelector)
      }

      iframeDoc.addEventListener('mouseover', handleMouseOver, true)
      iframeDoc.addEventListener('click', handleClick, true)

      setIsReady(true)

      // Cleanup
      return () => {
        iframeDoc.removeEventListener('mouseover', handleMouseOver, true)
        iframeDoc.removeEventListener('click', handleClick, true)
      }
    }

    iframe.addEventListener('load', handleLoad)

    return () => {
      iframe.removeEventListener('load', handleLoad)
    }
  }, [selectedElement, hoveredElement, generateSelector])

  const handleConfirm = () => {
    if (selector) {
      onSelect(selector)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <Target className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="font-semibold">Select Element</h3>
              <p className="text-sm text-muted-foreground">
                Click any element on the page to select it
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {selector && (
              <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 border border-blue-200 rounded-md">
                <code className="text-sm font-mono text-blue-700">{selector}</code>
              </div>
            )}
            <Button
              variant="outline"
              onClick={onCancel}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selector}
              className="gap-2"
            >
              <Check className="h-4 w-4" />
              Use This Selector
            </Button>
          </div>
        </div>
      </div>

      {/* Iframe */}
      <div className="pt-[73px] h-full">
        <iframe
          ref={iframeRef}
          src={storeUrl}
          className="w-full h-full border-0"
          title="Selector Picker"
        />
      </div>

      {/* Loading overlay */}
      {!isReady && (
        <div className="fixed inset-0 flex items-center justify-center bg-white/90 z-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-sm text-muted-foreground">Loading store...</p>
          </div>
        </div>
      )}
    </div>
  )
}
